import { DEFAULT_CURRENCY } from './currency';
import { ENVIRONMENT } from './constants';
import {
  sessionStorageGetItem,
  sessionStorageRemoveItem,
  sessionStorageSetItem,
} from './web-storage';

/* global dataLayer */

/**
 * Helper function to trigger event callback only once even when GA4 is enabled.
 *
 * See https://stackoverflow.com/questions/60011348/gtm-callback-firing-multiple-times
 */
export const buildGtmCallback = (callback) => {
  //GA4 is also a container, so need to fire callbacks only once, for GTM container
  //GA4 containerId starts with G-
  return (containerId) => {
    if (containerId.startsWith('GTM-') && typeof callback === 'function') {
      callback();
    }
  };
};
/**
 * The wrapper to do pushing GTM events by more reliable way.
 */
export const pushGtmEvent = (data) => {
  const isOnline = navigator.onLine;

  const waitForOnline = () => {
    const eventHandler = () => {
      window.removeEventListener('online', eventHandler);

      // The variable dataLayer is defined in "GoogleTagManager" component if GTM_ID
      // is passed. If that not defined, then "push" should not be executed.
      if (typeof dataLayer !== 'object') {
        return;
      }

      // Push.
      setTimeout(() => dataLayer.push(data), 500);
    };

    window.addEventListener('online', eventHandler);
  };

  // dataLayer.push doesn't have ways to check was it actually sent or not.
  // Let's check "navigator.onLine" before attempting to send GTM events,
  // theoretically it should increase reliability of this process.
  // Actually GTM library pushes the events when they appear in dataLayer,
  // but GTM library doesn't care about result of HTTP request that pushes
  // events. Let's do it only when we online.
  if (!isOnline) {
    waitForOnline();
    return;
  }

  // The variable dataLayer is defined in "GoogleTagManager" component if GTM_ID
  // is passed. If that not defined, then "push" should not be executed.
  if (typeof dataLayer !== 'object') {
    return;
  }

  // Push.
  dataLayer.push(data);
};

/**
 * We can't track page views very well by GTM in SPA, so
 * we have to send custom pageview event on every routeChangeComplete.
 */
export const pushPageview = () => {
  // Get or initialize global Cnet variable.
  window.Cnet = window.Cnet || {};
  window.Cnet.previousPath = window.Cnet.previousPath || null;

  // Workaround for https://github.com/zeit/next.js/issues/6025
  setTimeout(() => {
    // Workaround with previous path, because when location changes too quickly
    // (eg. automatic redirect from /checkout to /checkout/single)
    // because of delay in 1 second, it always pushes latest pathname
    // (eg. it pushes `/checkout/single` two times in this case)

    if (window.Cnet.previousPath !== location.pathname) {
      pushGtmEvent({ event: 'pageview' });
    }

    window.Cnet.previousPath = location.pathname;
  }, 1);
};

/**
 * Send a custom form submission event with given form id.
 *
 * We can't track default Gtm Form Submission event because of SPA.
 */
export const pushFormSubmission = (formId = 'undefined') => {
  pushGtmEvent({
    event: 'formSubmission',
    formID: formId,
  });
};

/**
 * Push checkout step to GTM.
 *
 * @see https://developers.google.com/tag-manager/enhanced-ecommerce#checkout
 * @see https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce#measuring-checkout
 */
export const pushCheckoutStep = (step = 1, option = '', products = []) => {
  pushGtmEvent({
    event: 'checkout',
    ecommerce: {
      currencyCode: DEFAULT_CURRENCY,
      checkout: {
        actionField: { step, option },
        products,
      },
    },
  });
};

/**
 * Push checkout option to GTM.
 *
 * @see https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce#measuring-checkout
 */
export const pushCheckoutOption = (step = 1, option = '') => {
  pushGtmEvent({
    event: 'checkoutOption',
    ecommerce: {
      checkout_option: {
        actionField: { step, option },
      },
    },
  });
};

/**
 * Push Product purchase to GTM.
 *
 * @see https://developers.google.com/tag-manager/enhanced-ecommerce#purchases
 */
export const pushPurchase = (transactionData, products = [], eventCallback, eventTimeout) => {
  // TODO: remove the part when A/B test is finished for the donation Modal.
  if (window._conv_q) {
    const goals = {
      production: {
        recurring: '1004119671',
        single: '1004119670',
      },
      development: {
        recurring: '1004119673',
        single: '1004119672',
      },
    };

    const type = products[0].variant.startsWith('single') ? 'single' : 'recurring';
    const waitPurchase = sessionStorageGetItem('cw_wait_purchase', false);
    if (transactionData.option === 'Modal' || waitPurchase) {
      window._conv_q.push(['pushRevenue', transactionData.revenue, 1, goals[ENVIRONMENT][type]]);
      sessionStorageRemoveItem('cw_wait_purchase');
    }
  }

  pushGtmEvent({
    event: 'purchase',
    ecommerce: {
      currencyCode: DEFAULT_CURRENCY,
      purchase: {
        actionField: transactionData,
        products,
      },
    },
    eventTimeout,
    eventCallback,
  });
};

/**
 * Push view_item event to GTM.
 *
 * @see https://developers.google.com/tag-manager/ecommerce-ga4?hl=ru#measure_viewsimpressions_of_productitem_details
 */
export const pushViewItem = (items) => {
  pushGtmEvent({
    event: 'view_item',
    ecommerce: {
      items,
    },
  });
};

/**
 * Push Money Handle click to GTM.
 */
export const moneyHandleClick = (
  position,
  blockName,
  appealTitle,
  amount,
  donationType,
  preselectedOption,
  impact,
) => {
  // Use existing DL variables gtm.element.dataset.* to avoid new one.
  pushGtmEvent({
    event: 'moneyHandleClick',
    gtm: {
      element: {
        dataset: {
          position,
          amount,
          title: appealTitle,
          category: `${blockName} ${donationType === 'single' || donationType === 'single_donation' ? 'Single' : 'Monthly'}`,
          preselected: preselectedOption || null,
          impact: impact || null,
        },
      },
    },
  });
};

export const pushDonationFormValidationErrors = (step, errors) => {
  // Note that we expose each error separately to simplify configuration on
  // GTM side.
  Object.keys(errors).forEach((field) => {
    if (typeof errors[field] === 'string') {
      pushGtmEvent({
        event: 'donationFormValidationError',
        step,
        field,
        error: errors[field],
      });
    } else {
      // Support 1 extra level of subfields.
      Object.keys(errors[field]).forEach((fieldLevel2) => {
        pushGtmEvent({
          event: 'donationFormValidationError',
          step,
          field: fieldLevel2,
          error: errors[field][fieldLevel2],
        });
      });
    }
  });
};

export const pushDonationFormSubmissionError = (paymentMethod, error) => {
  pushGtmEvent({
    event: 'donationFormSubmissionError',
    step: paymentMethod,
    error,
  });
};

/**
 * Push Santa Letter form validation errors to GTM.
 */
export const pushSantaLetterFormValidationErrors = (errors) => {
  Object.keys(errors).forEach((field) => {
    pushGtmEvent({
      event: 'santaLetterFormValidationError',
      field,
      error: errors[field],
    });
  });
};

/**
 * The function will be called when filters/search/sort for the Knowledge hub
 * functionality is updated.
 *
 * @param data
 */
export const pushKnowledgeHubFiltersUpdated = (data) => {
  // We need to clear value before pushing a new data to avoid
  // combining previous values with the data.
  pushGtmEvent({
    event: 'clearElementDataset',
    element: {
      dataset: undefined,
    },
  });

  // Each event must be pushed separately with "pushGtmEvent".
  pushGtmEvent({
    event: 'knowledgeHubFiltersUpdated',
    element: {
      dataset: data,
    },
  });
};

/**
 * Push an event when a user get the popup to return to a donation.
 */
export const pushReturnToDonationPopup = () => {
  const displayed = sessionStorageGetItem('displayedReturnToDonationPopup');
  if (!displayed) {
    pushGtmEvent({
      event: 'displayedReturnToDonationPopup',
    });
    sessionStorageSetItem('displayedReturnToDonationPopup', true);
  }
};

/**
 * Push an event when a user gets the browser prompt and decides to stay.
 */
export const pushReturnToDonationPrompt = () => {
  pushGtmEvent({
    event: 'returnToDonationAfterPrompt',
  });
};

/**
 * The function will be called when a user has changed an active item on slider.
 *
 * @param data
 */
export const pushSliderHasChanged = (data) => {
  // We need to clear value before pushing a new data to avoid
  // combining previous values with the data.
  pushGtmEvent({
    event: 'clearElementDataset',
    element: {
      dataset: undefined,
    },
  });

  pushGtmEvent({
    event: 'slide_has_changed',
    element: {
      dataset: data,
    },
  });
};

/**
 * Push Web Vitals to GTM.
 *
 * Based on https://www.simoahava.com/analytics/track-core-web-vitals-in-ga4-with-google-tag-manager/
 */
export const pushWebVitals = (metric) => {
  pushGtmEvent({
    event: 'coreWebVitals',
    webVitalsMeasurement: metric,
  });
};

/**
 * Get GTM Sessions ID.
 */
export const getSessionId = () => {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && window.gtag && window.google_tag_manager) {
      const targetId = Object.keys(window.google_tag_manager).find((key) => key.startsWith('G-'));

      if (targetId) {
        window.gtag('get', targetId, 'session_id', (sessionId) => {
          resolve(sessionId);
        });
      } else {
        resolve(null);
      }
    } else {
      resolve(null);
    }
  });
};

/**
 * Set GTM Sessions ID.
 */
export const setSessionId = (sessionId) => {
  // Find the GTM script in document.
  const gaScript = document.querySelector('script[src*="/gtm.js"]');
  if (gaScript) {
    // Wait when it will be loaded.
    gaScript.addEventListener('load', () => {
      function replaceSession() {
        if (!window.google_tag_manager) {
          return false;
        }

        const targetId = Object.keys(window.google_tag_manager).find((key) => key.startsWith('G-'));
        if (targetId) {
          window.gtag('config', targetId, {
            session_id: sessionId,
          });

          return true;
        }

        return false;
      }

      function isReadyToReplaceSession(attempt = 1) {
        if (replaceSession()) {
          return;
        }

        // Max - 15 attempts.
        if (attempt < 15) {
          setTimeout(isReadyToReplaceSession.bind(null, attempt + 1), attempt * attempt * 5);
        }
      }

      // First attempt to set new session id.
      isReadyToReplaceSession();
    });
  }
};
